import {
  Alert,
  AlertTitle,
  Button,
  CircularProgress,
  styled,
  TextField,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import { type ChangeHandler, useForm } from 'react-hook-form'

import { Box, Modal } from '@/components/ui'
import {
  INTAKE_FORM_NAME_MAX_LENGTH,
  type TemplateIssue,
} from '@/definitions/intakeForm'
import { useIntakeFormStarterTemplate } from '@/hooks/useIntakeFormStarterTemplate'
import { useSnackbar } from '@/hooks/useSnackbar'

import { useUploadIntakeForm } from '../../hooks/useUploadIntakeForm'
import { formatStarterTemplate } from '../../utils/formatStarterTemplate'

const MODAL_TITLE = 'New Intake Form'

const NAME_REQUIRED_MESSAGE = 'Give the form a name.'
const NAME_TOO_LONG_MESSAGE =
  `A name can be at most ${INTAKE_FORM_NAME_MAX_LENGTH} characters.`

const REFUSED_TITLE = 'This definition cannot be used'
const WARNED_TITLE = 'This definition will work, with the following caveats'

const DEFINITION_HELPER_TEXT =
  'The JSON produced by the SurveyJS designer. Paste over what is here.'
const EMPTY_DEFINITION_HELPER_TEXT = 'The JSON produced by the SurveyJS designer.'

const STARTER_UNAVAILABLE_MESSAGE =
  'The starting template could not be loaded, so the box below is empty. Paste a ' +
  'definition to add a form, or reopen this box to try again.'

// Styled rather than given a theme variant because TextField's `variant` prop
// selects the input component rather than a style.
const DefinitionField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-input': {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    fontSize: theme.typography.pxToRem(13),
    lineHeight: 1.5,
    whiteSpace: 'pre',
    overflowX: 'auto',
  },
}))

interface UploadIntakeFormValues {
  display_name: string
  form_template_json: string
}

interface UploadIntakeFormModalProps {
  orgId: string
  onClose: () => void
}

interface UploadIntakeFormBoxProps extends UploadIntakeFormModalProps {
  /** What the definition box starts with; empty when the starter did not load. */
  starterTemplateJson: string
}

const IssueList = ({ issues }: { issues: TemplateIssue[] }) => (
  <Box variant='flexColumn' gap='xs'>
    {issues.map((issue) => (
      <Typography key={issue.code} variant='body2'>
        {issue.message}
      </Typography>
    ))}
  </Box>
)

/**
 * The name-and-definition form, once it is known what the box starts with.
 *
 * Split from the component below so `defaultValues` is right on the first render
 * that shows a field: react-hook-form reads them once, and resetting the box from
 * a later arrival would race whatever the operator had already pasted into it.
 */
const UploadIntakeFormBox = ({
  orgId,
  onClose,
  starterTemplateJson,
}: UploadIntakeFormBoxProps) => {
  const { showSuccess } = useSnackbar()

  // Read once, like the box itself. A pre-fill that lands after the operator has
  // been given an empty box would otherwise withdraw the notice explaining it
  // while leaving the box empty, and relabel text they had started editing.
  const [openedWith] = useState(starterTemplateJson)
  const isStarterUnavailable = openedWith === ''

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<UploadIntakeFormValues>({
    defaultValues: {
      display_name: '',
      form_template_json: openedWith,
    },
  })

  const upload = useUploadIntakeForm(orgId, {
    onNameRefused: (message) => setError('display_name', { message }),
    onSaved: (displayName) => {
      showSuccess(`${displayName} was added. It is live at its patient link now.`)
      onClose()
    },
  })

  const withRetiredRefusal =
    (onChange: ChangeHandler): ChangeHandler =>
      (event) => {
        upload.retireRefusal()
        return onChange(event)
      }

  // `required` fails only on the empty string, so a name of spaces would reach the
  // API and come back as a validation error in a shape no refusal reader can use,
  // which would surface over the definition box instead of on the field it is about.
  const nameField = register('display_name', {
    validate: (value) => value.trim() !== '' || NAME_REQUIRED_MESSAGE,
    maxLength: { value: INTAKE_FORM_NAME_MAX_LENGTH, message: NAME_TOO_LONG_MESSAGE },
  })
  const definitionField = register('form_template_json')

  const onSubmit = (values: UploadIntakeFormValues) =>
    upload.submit({
      displayName: values.display_name,
      formTemplateJson: values.form_template_json,
    })

  return (
    <Modal
      title={MODAL_TITLE}
      onClose={onClose}
      disableClose={upload.isSaving}
      maxWidth='md'
      actions={
        <>
          <Button onClick={onClose} disabled={upload.isSaving}>
            Cancel
          </Button>
          <Button
            type='submit'
            form='upload-intake-form'
            variant='contained'
            disabled={upload.isSaving}
          >
            {upload.isAwaitingConfirmation ? 'Save anyway' : 'Save'}
          </Button>
        </>
      }
    >
      <Box
        component='form'
        id='upload-intake-form'
        variant='flexColumn'
        gap='md'
        onSubmit={handleSubmit(onSubmit)}
      >
        <TextField
          {...nameField}
          onChange={withRetiredRefusal(nameField.onChange)}
          label='Display name'
          fullWidth
          error={Boolean(errors.display_name)}
          helperText={errors.display_name?.message}
          disabled={upload.isSaving}
        />

        {isStarterUnavailable && (
          <Alert severity='info'>{STARTER_UNAVAILABLE_MESSAGE}</Alert>
        )}

        <DefinitionField
          {...definitionField}
          onChange={withRetiredRefusal(definitionField.onChange)}
          label='Form definition'
          helperText={
            isStarterUnavailable ? EMPTY_DEFINITION_HELPER_TEXT : DEFINITION_HELPER_TEXT
          }
          multiline
          minRows={14}
          maxRows={22}
          fullWidth
          disabled={upload.isSaving}
        />

        {upload.problem !== null && <Alert severity='error'>{upload.problem}</Alert>}

        {upload.isAwaitingConfirmation ? (
          <Alert severity='warning'>
            <AlertTitle>{WARNED_TITLE}</AlertTitle>
            <IssueList issues={upload.warnings} />
          </Alert>
        ) : (
          upload.refusal !== null && (
            <>
              <Alert severity='error'>
                <AlertTitle>{REFUSED_TITLE}</AlertTitle>
                {upload.refusal.errors === undefined ? (
                  <Typography variant='body2'>{upload.refusal.message}</Typography>
                ) : (
                  <IssueList issues={upload.refusal.errors} />
                )}
              </Alert>
              {/* A refusal carries its caveats as well as its reasons, so both are
                  shown in one pass rather than the caveats arriving as a second
                  refusal after the first is fixed. */}
              {upload.warnings.length > 0 && (
                <Alert severity='warning'>
                  <AlertTitle>{WARNED_TITLE}</AlertTitle>
                  <IssueList issues={upload.warnings} />
                </Alert>
              )}
            </>
          )
        )}

      </Box>
    </Modal>
  )
}

/**
 * Adds a custom intake form to an organization.
 *
 * The API refuses a definition carrying warnings until they have been shown, so a
 * warned definition takes a second press and the caveats are seen before the form
 * is live. Nothing here decides what is acceptable — every verdict comes from the
 * refusal, which names its reasons separately for exactly this.
 *
 * The definition the box starts from is fetched rather than bundled, so it ships
 * with the validator that has to accept it. A pre-fill that does not arrive leaves
 * an empty box rather than a closed one: pasting over it was the usual move anyway.
 */
export const UploadIntakeFormModal = ({
  orgId,
  onClose,
}: UploadIntakeFormModalProps) => {
  const starterTemplate = useIntakeFormStarterTemplate()

  // Latched rather than read live, because the two branches below render
  // different elements in the same position: returning to the loading one would
  // unmount the box and take the operator's pasted definition with it. A query
  // holding no data re-enters `pending` on any refetch, so this is reachable
  // even though the request has already settled once.
  //
  // A paused query counts as settled. Offline, the fetch is never sent, so
  // `pending` would otherwise hold the spinner until the browser reconnects —
  // and the request timeout that bounds every other wait never starts.
  const [hasStarterSettled, setHasStarterSettled] = useState(false)
  if (!hasStarterSettled && (!starterTemplate.isPending || starterTemplate.isPaused)) {
    setHasStarterSettled(true)
  }

  if (!hasStarterSettled) {
    return (
      <Modal
        title={MODAL_TITLE}
        onClose={onClose}
        maxWidth='md'
        actions={<Button onClick={onClose}>Cancel</Button>}
      >
        <Box variant='flexCenter'>
          <CircularProgress />
        </Box>
      </Modal>
    )
  }

  // Whether there is a pre-fill is decided by what the box would actually hold,
  // not by the request having succeeded: a 200 carrying no definition serializes
  // to nothing, which would otherwise reach the box as an unexplained empty one.
  return (
    <UploadIntakeFormBox
      orgId={orgId}
      onClose={onClose}
      starterTemplateJson={formatStarterTemplate(starterTemplate.data?.form_template)}
    />
  )
}
